import React, { useEffect, useRef, useState } from 'react'
import type { PublicVideoRequest } from 'apollo-gql'
import cn from 'classnames'
import { useTranslation } from 'lib/i18n'
import trackVideoRequestPageViewed from 'lib/tracking/avo/trackVideoRequestPageViewed'
import trackVideoRequestPlayed from 'lib/tracking/avo/trackVideoRequestPlayed'

import CardWithLargeVideo from 'components/CardWithLargeVideo/CardWithLargeVideo'
import CtaButton from 'components/CtaButton/CtaButton'
import Link from 'components/Link/Link'
import MemmoPlayer from 'components/MemmoPlayer/MemmoPlayer'
import RelatedProfileCollections from 'components/RelatedProfileCollections/RelatedProfileCollections'
import StickyBox from 'components/StickyBox/StickyBox'
import { Button } from 'components/UI/Button/Button'
import Wrapper from 'components/Wrapper/Wrapper'
import useAddToMetric from 'hooks/useAddToMetric'
import useCustomerData from 'hooks/useCustomerData'
import useEffectOnce from 'hooks/useEffectOnce'
import useResponsive from 'hooks/useResponsive'
import useReviewForRequest from 'hooks/useReviewForRequest'
import useVisitorType from 'hooks/useVisitorType'

import CmsContent from './CmsContent'
import VideoActionButtons from './VideoActionButtons'
import VideoInformation from './VideoInformation'
import VideoInformationBusiness from './VideoInformationBusiness'
import VideoInformationBusinessInternal from './VideoInformationBusinessInternal'
import VideoShare from './VideoShare'

import styles from './VideoRequestPage.module.css'

type VideoRequestPageProps = {
  request: PublicVideoRequest
  shortId: string
}

export default function VideoRequestPage({
  request,
}: VideoRequestPageProps): JSX.Element {
  const [addToMetric] = useAddToMetric()
  const { isMobile } = useResponsive()
  const { t } = useTranslation()
  const { isBuyer } = useVisitorType()
  const { publicProfile: profile, video, shortId } = request
  const [videoIsPlaying, setVideoIsPlaying] = useState(false)
  const [videoHasEnded, setVideoHasEnded] = useState(false)
  const contentRef = useRef(null)
  const videoPlayerRef = useRef(null)
  const customerData = useCustomerData()

  useEffectOnce(() => {
    trackVideoRequestPageViewed({ request })
  })

  const { review, beenViewed, refetch } = useReviewForRequest({
    shortId,
    requestId: request.id,
  })

  /**
   * After a video is played, a review is created with status NEW
   * as a side effect of addToMetric API call.
   * We need to re-fetch the review after the video ended
   * in order to retrieve its secret id to compose the url.
   */
  useEffect(() => {
    if (Boolean(refetch) && videoHasEnded) {
      refetch()
    }
  }, [videoHasEnded, refetch])

  const onPlay = () => {
    addToMetric('REQUEST_VIDEO_PLAY', request.id, 1)
    trackVideoRequestPlayed({ request })

    if (customerData && request.orderId) {
      customerData.logEvent('video-play', {
        customerId: request.customerId,
        fromName: request.fromName,
        isGift: request.isGift,
        orderId: request.orderId,
        profileId: request.publicProfile.id,
        requestId: request.id,
        shortId: request.shortId,
        toName: request.toName,
        isBuyer,
      })
    }
  }

  const shouldShowReviewButton = () => {
    if (!review) {
      return false
    }

    if (review.status !== 'NEW') {
      return false
    }

    return videoHasEnded || beenViewed
  }

  const isBusinessRequestInternal =
    request?.product?.slug === 'video-request-business-internal'

  const isBusinessRequest = request?.product?.slug === 'video-greeting-business'

  const VideoInformationComponent = () => {
    const VideoInformationComponentProps = {
      showReviewButton: shouldShowReviewButton(),
      request,
      review,
      contentRef,
      videoIsPlaying,
      videoPlayerRef,
    }

    if (isBusinessRequestInternal) {
      return (
        <VideoInformationBusinessInternal {...VideoInformationComponentProps} />
      )
    }

    if (isBusinessRequest) {
      return <VideoInformationBusiness {...VideoInformationComponentProps} />
    }
    return <VideoInformation {...VideoInformationComponentProps} />
  }

  return (
    <>
      <div className={styles.viewWrapper} data-cy="videoRequestPage">
        <div
          className={cn(styles.video, {
            [styles.videoMobile]: isMobile,
          })}
        >
          <div
            className={cn({
              [styles.cardAndStickyChatContainer]:
                isBusinessRequest || isBusinessRequestInternal,
            })}
          >
            <div>
              <CardWithLargeVideo
                videoComponent={() => (
                  <MemmoPlayer
                    ref={videoPlayerRef}
                    autoPlay={false}
                    onPlay={onPlay}
                    video={video}
                    handleOnVideoIsPlaying={() => setVideoIsPlaying(true)}
                    handleOnVideoIsPaused={() => setVideoIsPlaying(false)}
                    handleOnVideoEnd={() => setVideoHasEnded(true)}
                    text={t('VideoRequestPage.playAgain', 'Play again')}
                  >
                    {!videoIsPlaying && (
                      <div className={styles.cardContainer}>
                        <div className={styles.cardOverlay} />
                      </div>
                    )}
                  </MemmoPlayer>
                )}
              >
                <VideoInformationComponent />
              </CardWithLargeVideo>
            </div>
            {request?.requestType === 'VIDEO_BUSINESS' && (
              <div className={styles.stickyChatContainer}>
                <StickyBox
                  oritentation="right"
                  titleText={t(
                    'bareVideoRequestFormBusiness.help',
                    'Need some help?',
                  )}
                >
                  <CtaButton type="ghost" href="mailto:b2b@memmo.me">
                    {t('bareVideoRequestFormBusiness.contactUs', 'Contact us')}
                  </CtaButton>
                </StickyBox>
              </div>
            )}
          </div>
        </div>
      </div>

      <div ref={contentRef}>
        {isMobile && (
          <>
            <VideoActionButtons
              showReviewButton={shouldShowReviewButton()}
              review={review}
              request={request}
            />
            <VideoShare
              request={request}
              className={styles.videoInfoSharing}
              hideSocialMediaLinks={isBusinessRequestInternal}
            />
          </>
        )}
        <Wrapper edgeToEdge>
          {request?.requestType === 'VIDEO_BUSINESS' && (
            <CmsContent slug="video-request-business-only" />
          )}
          <CmsContent slug="video-request" />
          <RelatedProfileCollections
            profileSlug={profile.slug}
            noMoreLink
            limit={1}
            title={t(
              'VideoRequestPage.profileCollectionsSimilar',
              'People who bought {{name}} also bought from...',
              {
                name: profile.name,
              },
            )}
          />
          <div className={styles.browseOtherCtaButton}>
            <Link href="/search">
              <Button size="large" type="primary">
                {t(
                  'VideoRequestPage.browseOtherCelebrities',
                  'Browse other Celebrities',
                )}
              </Button>
            </Link>
          </div>
        </Wrapper>
      </div>
    </>
  )
}
